import React, { useState } from 'react';
import { FileText, Lock, ShieldCheck, Trash2, X } from 'lucide-react';

interface MemoryConsentModalProps {
  show: boolean;
  onClose: () => void;
  onAccept: () => void;
}

export default function MemoryConsentModal({ show, onClose, onAccept }: MemoryConsentModalProps) {
  const [agreed, setAgreed] = useState(false);

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Enable Autonomous Agent Memory</h2>
          <button onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-96 space-y-6 overflow-y-auto px-6 py-5">
          <p className="text-sm text-gray-600">
            Aegis AI can use the Cloudflare edge to distill workflow context into Knowledge Items.
            This improves agent responses while keeping the stored memory isolated and reviewable.
          </p>

          <div className="space-y-4 border-l-2 border-indigo-500 pl-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" />
              <p className="text-sm text-gray-700"><strong>1. Encryption in transit and at rest.</strong> Memory data is stored securely in the edge stack.</p>
            </div>
            <div className="flex items-start gap-3">
              <Lock className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" />
              <p className="text-sm text-gray-700"><strong>2. Strict tenancy.</strong> Memory is partitioned per user identity and not shared across tenants.</p>
            </div>
            <div className="flex items-start gap-3">
              <FileText className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" />
              <p className="text-sm text-gray-700"><strong>3. Distillation access.</strong> Background workers may summarize raw memory into higher-level knowledge artifacts.</p>
            </div>
            <div className="flex items-start gap-3">
              <Trash2 className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" />
              <p className="text-sm text-gray-700"><strong>4. Right to erasure.</strong> Stored memory can be exported and deleted through the privacy workflow.</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          <label className="flex items-center gap-2 text-xs text-gray-700">
            <input
              id="consent"
              type="checkbox"
              checked={agreed}
              onChange={() => setAgreed((prev) => !prev)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            I agree to the Agent Memory Terms.
          </label>

          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              Decline
            </button>
            <button
              disabled={!agreed}
              onClick={onAccept}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Enable Memory
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
